import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiCalendar,
  FiUsers,
  FiActivity,
  FiClock,
} from 'react-icons/fi';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaSyncAlt,
  FaList,
  FaTrophy,
  FaStar,
  FaLock,
  FaArrowUp,
} from 'react-icons/fa';
import apiService, { AdminStats } from '../services/apiService';

const DashboardOverview: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAdminStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const quickActions = [
    {
      title: 'Add New Series',
      description: 'Create and schedule a new series',
      icon: FiCalendar,
      color: 'blue',
      link: '/dashboard/series',
    },
    {
      title: 'Schedule Match',
      description: 'Add a new match to existing series',
      icon: FiActivity,
      color: 'green',
      link: '/dashboard/matches',
    },
    {
      title: 'Manage Teams',
      description: 'View and edit team information',
      icon: FiUsers,
      color: 'purple',
      link: '/dashboard/series',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <FaSyncAlt className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FaTimesCircle className="h-12 w-12 text-red-500 mx-auto" />
          <p className="mt-4 text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchStats}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const overviewCards = [
    {
      title: 'Total Series',
      value: stats.overview.totalSeries,
      subtitle: `${stats.series.published} published`,
      icon: FiCalendar,
      bgColor: 'bg-blue-50 dark:bg-primary-500/20',
      iconColor: 'text-blue-600 dark:text-primary-400',
      borderColor: 'border-blue-200 dark:border-primary-500/30',
    },
    {
      title: 'Total Matches',
      value: stats.overview.totalMatches,
      subtitle: `${stats.matches.today} today`,
      icon: FiActivity,
      bgColor: 'bg-green-50 dark:bg-success-500/20',
      iconColor: 'text-green-600 dark:text-success-500',
      borderColor: 'border-green-200 dark:border-success-500/30',
    },
    {
      title: 'Total Users',
      value: stats.overview.totalUsers,
      subtitle: `${stats.users.newUsers.last30Days} new (30d)`,
      icon: FiUsers,
      bgColor: 'bg-purple-50 dark:bg-secondary-500/20',
      iconColor: 'text-purple-600 dark:text-secondary-400',
      borderColor: 'border-purple-200 dark:border-secondary-500/30',
    },
    {
      title: 'Total Contests',
      value: stats.overview.totalContests,
      subtitle: `${stats.overview.totalFantasyTeams} teams`,
      icon: FaTrophy,
      bgColor: 'bg-orange-50 dark:bg-warning-500/20',
      iconColor: 'text-orange-600 dark:text-warning-500',
      borderColor: 'border-orange-200 dark:border-warning-500/30',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard Overview</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Welcome back! Here's what's happening with your cricket management system.
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
        >
          <FaSyncAlt className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Overview Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {overviewCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border ${stat.borderColor} shadow-sm dark:shadow-lg hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-primary-500/10 transition-all duration-200 overflow-hidden`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{stat.value}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{stat.subtitle}</p>
                  </div>
                  <div className={`${stat.bgColor} p-3 rounded-lg backdrop-blur-sm`}>
                    <Icon className={`h-8 w-8 ${stat.iconColor}`} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Series Breakdown - Takes 1 column */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaList className="h-5 w-5" />
              Series Overview
            </h2>
            <Link
              to="/dashboard/series"
              className="text-sm font-medium text-blue-600 dark:text-primary-400 hover:text-blue-700 dark:hover:text-primary-300 transition-colors"
            >
              View All →
            </Link>
          </div>
          <div className="p-6 space-y-4">
            {/* Status */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 dark:bg-success-500/10 rounded-lg border border-green-100 dark:border-success-500/20">
                <p className="text-xs font-medium text-green-600 dark:text-success-500 mb-1">Published</p>
                <p className="text-xl font-bold text-green-700 dark:text-success-400">{stats.series.published}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-500/10 rounded-lg border border-gray-100 dark:border-gray-500/20">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Unpublished</p>
                <p className="text-xl font-bold text-gray-700 dark:text-gray-300">{stats.series.unpublished}</p>
              </div>
            </div>

            {/* By Type */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">By Type</p>
              <div className="space-y-2">
                {Object.entries(stats.series.byType).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{type}</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Format */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">By Format</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.series.byFormat).map(([format, count]) => (
                  <span
                    key={format}
                    className="px-2.5 py-1 text-xs font-medium bg-blue-50 dark:bg-primary-500/10 text-blue-700 dark:text-primary-400 rounded-full border border-blue-100 dark:border-primary-500/20"
                  >
                    {format}: {count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Matches Breakdown - Takes 1 column */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FiActivity className="h-5 w-5" />
              Matches Overview
            </h2>
            <Link
              to="/dashboard/matches"
              className="text-sm font-medium text-blue-600 dark:text-primary-400 hover:text-blue-700 dark:hover:text-primary-300 transition-colors"
            >
              View All →
            </Link>
          </div>
          <div className="p-6 space-y-4">
            {/* Key Numbers */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 dark:bg-primary-500/10 rounded-lg border border-blue-100 dark:border-primary-500/20">
                <p className="text-xs font-medium text-blue-600 dark:text-primary-400 mb-1">Upcoming</p>
                <p className="text-xl font-bold text-blue-700 dark:text-primary-300">{stats.matches.upcoming}</p>
              </div>
              <div className="p-3 bg-orange-50 dark:bg-warning-500/10 rounded-lg border border-orange-100 dark:border-warning-500/20">
                <p className="text-xs font-medium text-orange-600 dark:text-warning-500 mb-1">Today</p>
                <p className="text-xl font-bold text-orange-700 dark:text-warning-400">{stats.matches.today}</p>
              </div>
            </div>

            {/* By State */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">By State</p>
              <div className="space-y-2">
                {Object.entries(stats.matches.byState).map(([state, count]) => {
                  const stateColors: Record<string, string> = {
                    Upcoming: 'bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400',
                    Complete: 'bg-green-100 dark:bg-success-500/20 text-green-700 dark:text-success-400',
                    Preview: 'bg-yellow-100 dark:bg-warning-500/20 text-yellow-700 dark:text-warning-400',
                    Live: 'bg-red-100 dark:bg-danger-500/20 text-red-700 dark:text-danger-400',
                  };
                  return (
                    <div key={state} className="flex items-center justify-between">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${stateColors[state] || 'bg-gray-100 text-gray-700'}`}>
                        {state}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Takes 1 column */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                const colorClasses = {
                  blue: 'bg-blue-50 dark:bg-primary-500/10 hover:bg-blue-100 dark:hover:bg-primary-500/20 border-blue-100 dark:border-primary-500/20 text-blue-700 dark:text-primary-400',
                  green: 'bg-green-50 dark:bg-success-500/10 hover:bg-green-100 dark:hover:bg-success-500/20 border-green-100 dark:border-success-500/20 text-green-700 dark:text-success-500',
                  purple: 'bg-purple-50 dark:bg-secondary-500/10 hover:bg-purple-100 dark:hover:bg-secondary-500/20 border-purple-100 dark:border-secondary-500/20 text-purple-700 dark:text-secondary-400',
                };

                return (
                  <Link
                    key={index}
                    to={action.link}
                    className={`block p-4 rounded-lg border transition-all duration-200 ${
                      colorClasses[action.color as keyof typeof colorClasses]
                    }`}
                  >
                    <div className="flex items-start">
                      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div className="ml-3">
                        <p className="font-semibold text-sm">{action.title}</p>
                        <p className="text-xs mt-1 opacity-80">{action.description}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Stats */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FiUsers className="h-5 w-5" />
              Users
            </h2>
            <Link
              to="/dashboard/users"
              className="text-sm font-medium text-blue-600 dark:text-primary-400 hover:text-blue-700 dark:hover:text-primary-300 transition-colors"
            >
              View All →
            </Link>
          </div>
          <div className="p-6 space-y-4">
            <div className="text-center p-4 bg-purple-50 dark:bg-secondary-500/10 rounded-lg border border-purple-100 dark:border-secondary-500/20">
              <p className="text-3xl font-bold text-purple-700 dark:text-secondary-400">{stats.users.total}</p>
              <p className="text-sm text-purple-600 dark:text-secondary-500">Total Users</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">New Users</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-400/30 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Last 24 hours</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                    {stats.users.newUsers.last24Hours > 0 && <FaArrowUp className="h-3 w-3 text-green-500" />}
                    {stats.users.newUsers.last24Hours}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-400/30 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Last 7 days</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                    {stats.users.newUsers.last7Days > 0 && <FaArrowUp className="h-3 w-3 text-green-500" />}
                    {stats.users.newUsers.last7Days}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-400/30 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Last 30 days</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                    {stats.users.newUsers.last30Days > 0 && <FaArrowUp className="h-3 w-3 text-green-500" />}
                    {stats.users.newUsers.last30Days}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contests Stats */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaTrophy className="h-5 w-5" />
              Contests
            </h2>
            <Link
              to="/dashboard/contests"
              className="text-sm font-medium text-blue-600 dark:text-primary-400 hover:text-blue-700 dark:hover:text-primary-300 transition-colors"
            >
              View All →
            </Link>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 dark:bg-success-500/10 rounded-lg border border-green-100 dark:border-success-500/20">
                <p className="text-xs font-medium text-green-600 dark:text-success-500 mb-1">Public</p>
                <p className="text-xl font-bold text-green-700 dark:text-success-400">{stats.contests.publicContests}</p>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-secondary-500/10 rounded-lg border border-purple-100 dark:border-secondary-500/20">
                <p className="text-xs font-medium text-purple-600 dark:text-secondary-500 mb-1">Private</p>
                <p className="text-xl font-bold text-purple-700 dark:text-secondary-400">{stats.contests.privateContests}</p>
              </div>
            </div>

            {/* By Status */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">By Status</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.contests.byStatus).map(([status, count]) => (
                  <span
                    key={status}
                    className="px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-dark-300/50 text-gray-700 dark:text-gray-300 rounded-full capitalize"
                  >
                    {status}: {count}
                  </span>
                ))}
              </div>
            </div>

            {/* By Type */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">By Type</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.contests.byType).map(([type, count]) => (
                  <span
                    key={type}
                    className="px-2.5 py-1 text-xs font-medium bg-orange-50 dark:bg-warning-500/10 text-orange-700 dark:text-warning-400 rounded-full"
                  >
                    {type.replace(/_/g, ' ')}: {count}
                  </span>
                ))}
              </div>
            </div>

            {/* Locked */}
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-400/30 rounded">
              <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                <FaLock className="h-4 w-4" /> Locked Teams
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.contests.locked.teams}</span>
            </div>
          </div>
        </div>

        {/* Fantasy Teams */}
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FaStar className="h-5 w-5" />
              Fantasy Teams
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="text-center p-4 bg-orange-50 dark:bg-warning-500/10 rounded-lg border border-orange-100 dark:border-warning-500/20">
              <p className="text-3xl font-bold text-orange-700 dark:text-warning-400">{stats.fantasyTeams.total}</p>
              <p className="text-sm text-orange-600 dark:text-warning-500">Total Teams</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">Per Contest</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-400/30 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Average</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.fantasyTeams.avgPerContest}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-400/30 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Maximum</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.fantasyTeams.maxPerContest}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-400/30 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Minimum</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.fantasyTeams.minPerContest}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scheduler Section */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FiClock className="h-5 w-5" />
            Scheduler Status (Last 24 Hours)
          </h2>
          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
            stats.scheduler.last24Hours.successRate === 100
              ? 'bg-green-100 dark:bg-success-500/20 text-green-700 dark:text-success-400'
              : stats.scheduler.last24Hours.successRate >= 90
              ? 'bg-yellow-100 dark:bg-warning-500/20 text-yellow-700 dark:text-warning-400'
              : 'bg-red-100 dark:bg-danger-500/20 text-red-700 dark:text-danger-400'
          }`}>
            {stats.scheduler.last24Hours.successRate}% Success Rate
          </span>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-gray-50 dark:bg-dark-400/30 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.scheduler.last24Hours.totalRuns}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Runs</p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-success-500/10 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-700 dark:text-success-400">{stats.scheduler.last24Hours.successfulRuns}</p>
              <p className="text-sm text-green-600 dark:text-success-500">Successful</p>
            </div>
            <div className="p-4 bg-red-50 dark:bg-danger-500/10 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-700 dark:text-danger-400">{stats.scheduler.last24Hours.failedRuns}</p>
              <p className="text-sm text-red-600 dark:text-danger-500">Failed</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-primary-500/10 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-700 dark:text-primary-400">{stats.scheduler.last24Hours.successRate}%</p>
              <p className="text-sm text-blue-600 dark:text-primary-500">Success Rate</p>
            </div>
          </div>

          {/* Job Summary & Recent Jobs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Job Summary */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Job Summary</p>
              <div className="space-y-2">
                {Object.entries(stats.scheduler.jobSummary).map(([jobName, summary]) => (
                  <div
                    key={jobName}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{jobName}</span>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-sm text-green-600 dark:text-success-500">
                        <FaCheckCircle className="h-4 w-4" /> {summary.success}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-red-600 dark:text-danger-500">
                        <FaTimesCircle className="h-4 w-4" /> {summary.error}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Jobs */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Recent Jobs</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {stats.scheduler.recentJobs.map((job, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-400/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {job.status === 'success' ? (
                        <FaCheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <FaTimesCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{job.jobName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{job.message}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(job.runAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        Last updated: {formatDate(stats.generatedAt)}
      </div>
    </div>
  );
};

export default DashboardOverview;
